import { apiService } from '@/services/api/apiService';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

interface DocumentType {
    areaCodeName: string;
    code: string;
    documentTypeName: string;
    id: string | number;
}

interface DocumentTypeState {
    data: DocumentType[];
    loading: boolean;
    error: string | null;
}

const KYCPORT = import.meta.env.VITE_KYC_PORT;
const KYCURL = import.meta.env.VITE_API_KYC_URL;

interface ApiResponse {
    data: DocumentType[];
}

const initialState: DocumentTypeState = {
    data: [],
    loading: false,
    error: null,
};

// ✅ Fetch document types
export const fetchDocumentType = createAsyncThunk<
    DocumentType[],
    void,
    { rejectValue: string }
>('documentType/fetchDocumentType', async (_, { rejectWithValue }) => {
    try {
        const response = await apiService.get<ApiResponse>(
            '/get_all_kyc_document_type',
            KYCPORT,
            KYCURL
        );
        return response.data;
    } catch (error) {
        return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
});

// ✅ Create document type
export const createDocumentType = createAsyncThunk<
    DocumentType, // expect API to return a single created document
    Partial<DocumentType>,
    { rejectValue: string }
>('documentType/createDocumentType', async (params, { rejectWithValue }) => {
    try {
        const response = await apiService.post<DocumentType>(
            'create_kyc_document_type',
            params,
            KYCPORT,
            KYCURL
        );
        return response;
    } catch (error: any) {
        return rejectWithValue(error.message);
    }
});

// ✅ Update document type
export const updateDocumentType = createAsyncThunk<
    DocumentType,
    Partial<DocumentType>,
    { rejectValue: string }
>('documentType/updateDocumentType', async (params, { rejectWithValue }) => {
    try {
        const response = await apiService.put<DocumentType>(
            'update_kyc_document_type',
            params,
            KYCPORT,
            KYCURL
        );
        return response;
    } catch (error: any) {
        return rejectWithValue(error.message);
    }
});


export const softDeleteKycDocumetType: any = createAsyncThunk<
    number,
    number
>(
    "kycDocumetType/softDeleteKycDocumetType",
    async (id,) => {
        try {
            const response = await apiService.patch(
                `soft_delete_kyc_document_type?id=${id}`,
                {},
                KYCPORT,
                KYCURL
            );

            return response



        } catch (error: any) {
            return (
                error?.message || "An error occurred while deleting cuisine type"
            );
        }
    }
);

// ✅ Slice
export const DocumentTypeSlice = createSlice({
    name: 'documentType',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Fetch
            .addCase(fetchDocumentType.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDocumentType.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchDocumentType.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? action.error.message ?? 'Failed to fetch document types';
            })

            // Create
            .addCase(createDocumentType.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createDocumentType.fulfilled, (state, action) => {
                state.loading = false;
                state.data.push(action.payload); // ✅ append new doc type
            })
            .addCase(createDocumentType.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? action.error.message ?? 'Failed to create document type';
            })

            // Update
            .addCase(updateDocumentType.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateDocumentType.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.data.findIndex((d) => d.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload; // ✅ replace updated item
                }
            })
            .addCase(updateDocumentType.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? action.error.message ?? 'Failed to update document type';
            });
    },
});

export default DocumentTypeSlice.reducer;
